import { useCreateApprovingInvoiceReportApprovalMutation } from '@/context/services/approving_invoice_reports/ApprovingInvoiceReports.service';
import { useApprovingInvoiceReportsSearchRefetch } from '@/context/services/approving_invoice_reports/refetchContext';
import { ApprovingInvoiceReportColumn } from '@/context/services/approving_invoice_reports/type';
import { useCheckApprovingReportsProtectedResources } from '@/features/approving_invoice_reports/components/hooks/useCheckApprovingReportsProtectedResources';
import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTriggerFindTargetReport } from '../../List/context/TriggerFindTargetReportContext';
import {
  ApproveSuccessAction,
  useConfirmApproveModal,
} from '../context/ConfirmApproveModalContext';

type UseApproveReportProps = {
  onDrawerOpen: (id: string) => void;
  onDrawerClose: () => void;
};

const statusCodesCheckAccessPermission = ['401', '403', '404'];

export const useApproveReport = ({
  onDrawerOpen,
  onDrawerClose,
}: UseApproveReportProps) => {
  const notify = useToastAPI();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { approveSuccessAction } = useConfirmApproveModal();
  const { onTriggerFindTargetReport, onTriggerBackToPageOneIfNoData } =
    useTriggerFindTargetReport();
  const { mutateAsync } = useCreateApprovingInvoiceReportApprovalMutation();
  const refetch = useApprovingInvoiceReportsSearchRefetch();
  const { handleCheckPermission } =
    useCheckApprovingReportsProtectedResources();
  const { onCloseConfirmApproveModal } = useConfirmApproveModal();
  const onSuccessFoundNextTargetReport = useCallback(
    (currentReportId: string) => {
      return (report: ApprovingInvoiceReportColumn) => {
        if (report.id === currentReportId) {
          queryClient.invalidateQueries({
            queryKey: ['approvingInvoiceReportSummary', currentReportId],
          });
        }
        onDrawerOpen(report.id);
      };
    },
    [onDrawerOpen, queryClient]
  );

  const onErrorFoundNextTargetReport = useCallback(() => {
    onDrawerClose();
  }, [onDrawerClose]);

  const onCheckAccessApprovingReportSuccess = useCallback(
    (message?: string) => () => {
      onCloseConfirmApproveModal();
      onDrawerClose();
      onTriggerBackToPageOneIfNoData();
      notify?.error({
        title: message || t('unexpected_error_approve_invoice_report'),
        duration: 3,
      });
    },
    [
      notify,
      t,
      onTriggerBackToPageOneIfNoData,
      onCloseConfirmApproveModal,
      onDrawerClose,
    ]
  );

  const onCheckAccessApprovingReportFailure = useCallback(() => {
    onDrawerClose();
  }, [onDrawerClose]);

  const approveReport = useCallback(
    (reportId: string) => async () => {
      if (!reportId?.trim()) {
        notify?.error({
          title: t('invalid_report_id_error'),
          duration: 3,
        });
        return;
      }

      const resp = await mutateAsync({ id: reportId });

      if (resp.status === 'success') {
        notify?.success({
          title: t('approve_invoice_report_success_message'),
          duration: 3,
        });
        // Handle different success actions based on context
        await refetch?.();
        if (approveSuccessAction === ApproveSuccessAction.GOTO_NEXT_TARGET) {
          onTriggerFindTargetReport(
            onSuccessFoundNextTargetReport(reportId),
            onErrorFoundNextTargetReport
          );
        }
        if (approveSuccessAction === ApproveSuccessAction.REFRESH_DATA) {
          onTriggerBackToPageOneIfNoData();
        }
      } else {
        if (
          statusCodesCheckAccessPermission.includes(resp.errors?.code || '')
        ) {
          handleCheckPermission({
            fnCheckPermission: refetch,
            onCheckSuccess: onCheckAccessApprovingReportSuccess(
              resp?.errors?.messages?.join?.('')
            ),
            onCheckFailure: onCheckAccessApprovingReportFailure,
          });
          return;
        } else {
          notify?.error({
            title:
              resp?.errors?.messages?.join(', ') ||
              t('unexpected_error_approve_invoice_report'),
            duration: 3,
          });
        }
      }
      onCloseConfirmApproveModal();
    },
    [
      mutateAsync,
      onCloseConfirmApproveModal,
      notify,
      t,
      refetch,
      approveSuccessAction,
      onTriggerFindTargetReport,
      onSuccessFoundNextTargetReport,
      onErrorFoundNextTargetReport,
      onTriggerBackToPageOneIfNoData,
      handleCheckPermission,
      onCheckAccessApprovingReportSuccess,
      onCheckAccessApprovingReportFailure,
    ]
  );

  return {
    approveReport,
  };
};
